/**
 * The runtime validator at the B1 client entry (W-11) — `server/lib/contractValidate.js`'s SPA
 * twin, same signature: `validate(schemaName, value)`, `assertValid(schemaName, value)` (throws
 * `WireShapeError`), `schemaNames()`.
 *
 * Validates a value against one of the forecast-row wire schemas in
 * `../types/forecastWire.schema.generated.json`, rendered from `server/repo/forecastRow.js` by
 * `scripts/gen-forecast-wire.mjs` beside the generated `ForecastWire` type (never hand-edited;
 * `scripts/check.sh --step wire-types`). The validators themselves are generated too:
 * `./wireValidate.generated.js` holds one standalone function per schema, emitted by ajv's
 * codegen at generation time under that script's `VALIDATOR_OPTIONS`, so this module compiles
 * nothing at runtime — no ajv in the bundle, no `new Function`, nothing a CSP without
 * `'unsafe-eval'` would refuse. No hand-written guards: a shape question is answered by
 * the schema the row shape declares. `format` keywords are not asserted. `validate()` and
 * `assertValid()` are STRICT: `ForecastWire` is closed (`additionalProperties: false`, C-14), so
 * an undeclared key such as `isMultiway` is `ok: false` here.
 *
 * `validateWireResponse` is the ENTRY POLICY `lib/apiClient.js` consults after parsing a body: a
 * `GET` 2xx on `/api/forecasts`, `/api/forecasts/shared` or `/api/forecasts/by-contract` whose
 * body carries a `forecasts` array has EVERY row graded against the route's schema — the walk
 * never stops early — and each row's errors fall into one of two classes (ruling R13):
 *
 * - EXTRA — every error is an `additionalProperties` error (a key the server added that this
 *   bundle's schema does not know, or a fixture invented): the row is kept, its unknown keys go
 *   into the verdict's `extra` (deduped over all rows), and apiClient logs ONE
 *   `console.warn("apiClient wire_shape_extra", …)` per response (warn: drift, not failure — R14)
 *   and passes the body through unchanged. The SPA has always ignored unknown keys, so this is
 *   observation, not a failed load.
 * - SHAPE — anything else (a wrong type on a known key, a missing `id`, a non-object row): THAT
 *   row goes into the verdict's `dropped` list with its first errors (`/forecasts/<i>/<field> …`)
 *   and apiClient DROPS IT from the body it hands back, loud — one
 *   `console.error("forecasts.wire_shape_failed", …)` per dropped row (capped) plus a session
 *   counter — while every other row hydrates. A read is never failed as a whole by a row.
 *
 * Consequence to know about: an SPA bundle still open across a deploy that ADDS a wire field
 * (a column in the row shape) logs `wire_shape_extra` on its list loads until the page is
 * reloaded; a deploy that retypes a known field, or drops `id`, drops THOSE rows from the list,
 * loud, until the page is reloaded — `apiClient` offers a guided reload (`lib/staleBundle.js`)
 * when rows are dropped — never the whole list. Dropping any other field is invisible here:
 * every key but `id` is optional (the lean projection omits most of them by design).
 */
import * as GENERATED from "./wireValidate.generated.js";
import type { WireValidateError, WireValidateFunction } from "./wireValidate.generated.js";

const MAX_REPORTED_ERRORS = 3;

/** The schema one `GET` route's `forecasts` rows must satisfy, by pathname. */
const WIRE_ROUTES: Readonly<Record<string, string>> = Object.freeze({
  "/api/forecasts": "ForecastWire",
  "/api/forecasts/shared": "SharedForecastWire",
  "/api/forecasts/by-contract": "ForecastRowAny",
  "/api/forecasts/by-id": "ForecastRowAny",
});

/**
 * The mixed `by-contract` array holds owner rows and shared rows, so a row is graded against the
 * ONE branch of `ForecastRowAny` its shared-row markers select (`isOwn` / `owner` — keys no owner
 * row carries), never against the kinder of the two: under the owner branch a shared row's
 * viewer keys are all "extra", which would let a mistyped `owner` or `canEdit` pass as drift.
 */
const BRANCH_OF: Readonly<Record<string, (row: unknown) => string>> = Object.freeze({
  ForecastRowAny: (row: unknown) =>
    row !== null && typeof row === "object" && ("isOwn" in row || "owner" in row)
      ? "SharedForecastWire"
      : "ForecastWire",
});

export class WireShapeError extends Error {
  schemaName: string;
  errors: string[];

  constructor(schemaName: string, errors: string[]) {
    const shown = errors.slice(0, MAX_REPORTED_ERRORS);
    super(`${schemaName}: ${shown.join("; ")}`);
    this.name = "WireShapeError";
    this.schemaName = schemaName;
    this.errors = shown;
  }
}

const { SCHEMA_NAMES, ...VALIDATORS } = GENERATED;

/** The schema names the generated document defines. */
export function schemaNames(): string[] {
  return [...SCHEMA_NAMES];
}

function validatorFor(schemaName: string): WireValidateFunction {
  if (!Object.prototype.hasOwnProperty.call(VALIDATORS, schemaName)) {
    throw new TypeError(`wireValidate: unknown wire schema "${schemaName}"`);
  }
  return VALIDATORS[schemaName as keyof typeof VALIDATORS];
}

function describe(err: WireValidateError): string {
  const where = err.instancePath || "/";
  const extra =
    err.keyword === "additionalProperties" && err.params && err.params.additionalProperty
      ? ` (${String(err.params.additionalProperty)})`
      : "";
  return `${where} ${err.message || err.keyword}${extra}`;
}

function rawValidate(schemaName: string, value: unknown): WireValidateError[] {
  const fn = validatorFor(schemaName);
  return fn(value) ? [] : [...(fn.errors || [])];
}

/** @param schemaName one of `schemaNames()` */
export function validate<T>(
  schemaName: string,
  value: T,
): { ok: true; value: T } | { ok: false; errors: string[] } {
  const errors = rawValidate(schemaName, value);
  if (errors.length === 0) return { ok: true, value };
  return { ok: false, errors: errors.map(describe) };
}

export function assertValid<T>(schemaName: string, value: T): T {
  const result = validate(schemaName, value);
  if (!result.ok) throw new WireShapeError(schemaName, result.errors);
  return result.value;
}

type RowGrade =
  | { kind: "ok" }
  | { kind: "extra"; extra: string[] }
  | { kind: "shape"; errors: string[] };

/** Grade one row against one closed schema: ok, extra keys only, or a shape error. */
function gradeAgainst(schemaName: string, row: unknown): RowGrade {
  const errors = rawValidate(schemaName, row);
  if (errors.length === 0) return { kind: "ok" };
  const isExtra = (err: WireValidateError) =>
    err.keyword === "additionalProperties" && err.instancePath === "";
  const shape = errors.filter((err) => !isExtra(err));
  // The shape report leaves the tolerated extras out, so the capped log names the real fault.
  if (shape.length > 0) return { kind: "shape", errors: shape.map(describe) };
  return { kind: "extra", extra: errors.map((err) => String(err.params.additionalProperty)) };
}

/** Grade one row against the route's schema — for a branched (`anyOf`) schema, its marker-selected branch. */
function gradeRow(schemaName: string, row: unknown): RowGrade {
  const pick = BRANCH_OF[schemaName];
  return gradeAgainst(pick ? pick(row) : schemaName, row);
}

/** One row a SHAPE error excludes: its index in `forecasts` and its first MAX_REPORTED_ERRORS described errors, each prefixed `/forecasts/<index>`. */
export type DroppedWireRow = { index: number; errors: string[] };

/**
 * The per-row verdict `validateWireResponse` returns. `schema` is the route's schema name when
 * the body was examined (absent: not examined, nothing to say); `extra` the unknown keys over
 * all rows (deduped, first-seen order) when any row carried one; `dropped` the rows a SHAPE
 * error excludes, in index order, when any did. There is no whole-read failure form: a caller
 * reads `dropped` and keeps the rest.
 */
export type WireResponseVerdict = {
  schema?: string;
  extra?: string[];
  dropped?: DroppedWireRow[];
};

/**
 * The route-level check `apiClient.request` applies to a parsed response. Only a `GET` with a
 * 2xx status on a route in WIRE_ROUTES whose body has a `forecasts` array is examined (anything
 * else is `{}`); every row of that array is graded against the route's schema and the walk never
 * stops early. The envelope's other keys (`pinned`, `view`, `next_page_token`) are not validated.
 * A row with a SHAPE error joins `dropped` as `{ index, errors }` — that row's first
 * MAX_REPORTED_ERRORS errors prefixed by its index, so the field path is in each string; a row
 * whose only fault is unknown keys adds them to `extra`. A clean examined body is `{ schema }`.
 */
export function validateWireResponse(
  method: string,
  url: string,
  status: number,
  body: unknown,
): WireResponseVerdict {
  if (method.toUpperCase() !== "GET" || status < 200 || status >= 300) return {};
  const schema = WIRE_ROUTES[new URL(url, "http://x").pathname];
  if (!schema) return {};
  if (!body || typeof body !== "object") return {};
  const rows = (body as { forecasts?: unknown }).forecasts;
  if (!Array.isArray(rows)) return {};
  const extra = new Set<string>();
  const dropped: DroppedWireRow[] = [];
  for (let i = 0; i < rows.length; i++) {
    const grade = gradeRow(schema, rows[i]);
    if (grade.kind === "shape") {
      dropped.push({
        index: i,
        errors: grade.errors.slice(0, MAX_REPORTED_ERRORS).map((e) => `/forecasts/${i}${e}`),
      });
    } else if (grade.kind === "extra") {
      for (const k of grade.extra) extra.add(k);
    }
  }
  const verdict: WireResponseVerdict = { schema };
  if (extra.size) verdict.extra = [...extra];
  if (dropped.length) verdict.dropped = dropped;
  return verdict;
}
